import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  Root as DialogRoot,
  DialogTrigger,
} from "@radix-ui/react-dialog";
import clsx from "clsx";
import {
  PaymentMethod,
  currencyGroups,
  networkIconMap,
  paymentMethods,
} from "constants/methods";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import { Search } from "ui-kit/icons/Search";
import { TriangleDown } from "ui-kit/icons/TriangleDown";
import { useTranslation } from "react-i18next";
import ChevronBack from "assets/svgComponents/buyTokenForm/ChevronBack";
import {
  gradientRowBgClass,
  shadowAndBorderClass,
} from "screens/Dashboard/utils/classes";
import CrossCloseIcon from "assets/svgComponents/buyTokenForm/CrossCloseIcon";
import {
  AnimatedGradientBorder,
  GlowCard,
} from "screens/Landing/components/shared";

interface TokensSelectProps {
  selected: PaymentMethod;
  onSelect: (method: PaymentMethod) => void;
  type?: "preselect";
  filteredMethods?: PaymentMethod[];
}

export function PaymentSelect({
  selected,
  onSelect,
  type,
  filteredMethods: propFilteredMethods,
}: TokensSelectProps) {
  const [isOpen, setOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [expandedMethod, setExpandedMethod] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleOpenChange = (val: boolean) => {
    setOpen(val);
    if (!val) {
      setSearchName("");
      setExpandedMethod(null);
    }
  };

  const handleSelect = (method: PaymentMethod) => {
    onSelect(method);
    handleOpenChange(false);
  };

  const uniqueCurrencies = useMemo(() => {
    const allMethods = propFilteredMethods || paymentMethods;
    const filteredBySearch = allMethods.filter(
      (method) =>
        method.currency.toLowerCase().includes(searchName.toLowerCase()) ||
        method.nowPaymentsId.toLowerCase().includes(searchName.toLowerCase()),
    );

    const seen = new Set();
    return filteredBySearch.filter((method) => {
      if (seen.has(method.currency)) {
        return false;
      }
      seen.add(method.currency);
      return true;
    });
  }, [searchName, propFilteredMethods]);

  const selectedNetworkIcon = networkIconMap[selected?.nowPaymentsId];

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      {type === "preselect" ? (
        <DialogTrigger asChild>
          <button
            className={clsx(
              "w-auto shrink-0 h-[42px] max-lg:h-[42px] p-[8px] max-lg:p-[8px] flex items-center justify-between bg-[#161616] rounded-[12px] before:!rounded-[12px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px]",
              shadowAndBorderClass,
            )}
          >
            {selected && (
              <div className="flex cursor-pointer shrink-0 gap-[12px] max-lg:gap-[12px] mr-[12px] items-center">
                <img
                  src={selected.icon}
                  alt={selected.name}
                  className="w-[29px] h-[29px] max-lg:w-[28px] max-lg:h-[28px]"
                />
                <div className="text-[16px] max-lg:text-14m font-luxora-regular text-white">
                  {selected.currency}
                </div>

                {selectedNetworkIcon && (
                  <div>
                    <img
                      src={selectedNetworkIcon}
                      alt={selected.nowPaymentsId}
                      className="w-[18px] h-[18px] shrink-0 max-lg:w-[16px] max-lg:h-[16px]"
                    />
                  </div>
                )}
              </div>
            )}
            <div
              className={clsx(
                "w-[26px] shrink-0 h-[26px] max-lg:w-[26px] max-lg:h-[26px] rounded-full before:!rounded-full bg-[#252525] outline-none flex justify-center items-center",
                shadowAndBorderClass,
              )}
            >
              <ChevronBack className="!rotate-[270deg] max-lg:w-[12px] max-lg:h-[12px] text-white" />
            </div>
          </button>
        </DialogTrigger>
      ) : (
        <DialogTrigger asChild>
          <button
            className={clsx(
              "flex items-center gap-[8px] rounded-full p-[8px] pr-[12px] outline-none bg-[#161616] before:!rounded-full",
              shadowAndBorderClass,
            )}
          >
            <img
              src={selected.icon}
              className="w-[20px] h-[20px]"
              alt={selected.nowPaymentsId}
            />
            <span className="font-luxora-regular text-sm text-white">
              {selected.nowPaymentsId}
            </span>
            <TriangleDown color="white" className="w-[16px] h-[16px]" />
          </button>
        </DialogTrigger>
      )}

      <AnimatePresence>
        {isOpen && (
          <DialogPortal forceMount>
            <DialogOverlay asChild>
              <motion.div className="fixed inset-0 z-[499] bg-black/50" />
            </DialogOverlay>
            <DialogContent
              className="!fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[500] w-[367px] max-lg:w-[360px] max-h-[90vh] max-lg:max-h-[80vh] h-auto rounded-[28px] max-lg:rounded-[12px] outline-none"
            >
              <AnimatedGradientBorder
                className="rounded-[28px] max-lg:rounded-[12px] w-full"
              >
                <GlowCard
                  className={clsx(
                    "p-[12px] max-lg:p-[8px] overflow-hidden flex flex-col bg-[#111111] h-full max-lg:rounded-[12px] max-lg:before:!rounded-[12px] rounded-[28px] before:!rounded-[28px] ring-1 ring-inset ring-white/[0.06]",
                  )}
                >
                  <motion.div
                    className={clsx(
                      "flex justify-between max-lg:mb-[24px] items-center max-lg:rounded-[12px] max-lg:before:!rounded-[12px] rounded-[16px] before:!rounded-[16px] p-[4px] max-lg:p-[5px] max-lg:pl-[12px] pl-[16px]",
                      gradientRowBgClass,
                    )}
                  >
                    <h2 className="text-white font-luxora-medium text-18 max-lg:text-20m">
                      {t("Select Currency")}
                    </h2>
                    <DialogTrigger asChild>
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className={clsx(
                          "w-[39px] h-[39px] max-lg:before:!rounded-[12px] max-lg:w-[39px] max-lg:h-[39px] max-lg:rounded-[12px] flex items-center justify-center before:!rounded-[12px] rounded-[12px] bg-[#111111]",
                          shadowAndBorderClass,
                        )}
                      >
                        <CrossCloseIcon
                          className="max-lg:w-[23px] max-lg:h-[23px] h-[20px] w-[20px]"
                          fill="white"
                        />
                      </motion.button>
                    </DialogTrigger>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className={clsx(
                      "flex items-center max-lg:w-full max-lg:mb-[24px] max-lg:h-[51px] max-lg:p-[16px] gap-[12px] lg:h-[51px] w-full",
                      shadowAndBorderClass,
                      "my-[16px] rounded-[16px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px] pl-[16px] before:!rounded-[16px] bg-[#161616]",
                    )}
                  >
                    <Search className="max-lg:w-[20px] max-lg:h-[20px] h-[20px] w-[20px]" />

                    <input
                      type="text"
                      className="grow font-medium max-lg:text-16m text-16
                    placeholder:max-lg:text-16m placeholder:text-16 leading-[100%] placeholder:leading-[100%]
                     text-white outline-none bg-transparent placeholder:text-white/40"
                      placeholder={t("Search")}
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                    />
                  </motion.div>

                  <div className="flex-1 overflow-y-auto scrollbar-dark">
                    <div className="flex flex-col gap-[8px] max-lg:gap-[8px]">
                      {uniqueCurrencies.map((method, index) => {
                        const networks = currencyGroups[method.currency] || [];
                        const hasMultipleNetworks = networks.length > 1;
                        const isExpanded = expandedMethod === method.currency;

                        return (
                          <motion.div key={method.currency}>
                            <button
                              className={clsx(
                                "p-[8px] max-lg:p-[8px] max-lg:min-h-[48px] min-h-[48px] text-left w-full max-lg:rounded-[12px] max-lg:before:!rounded-[12px] rounded-[16px] before:!rounded-[16px] bg-[#161616] relative",
                                shadowAndBorderClass,
                                "before:p-[0.5px]",
                              )}
                              onClick={() =>
                                hasMultipleNetworks
                                  ? setExpandedMethod(
                                      isExpanded ? null : method.currency,
                                    )
                                  : handleSelect(method)
                              }
                            >
                              <div className="flex items-center justify-between w-full cursor-pointer">
                                {" "}
                                <div className="flex items-center flex-grow gap-4 max-lg:gap-[8px]">
                                  <img
                                    src={method.icon}
                                    className="w-[32px] h-[32px] max-lg:w-[36px] max-lg:h-[36px]"
                                    alt={method.name}
                                  />
                                  <span className="font-luxora-regular text-16 max-lg:text-16m text-white">
                                    {method.currency}
                                  </span>
                                </div>
                                <div className="flex items-center gap-[8px] max-lg:gap-[8px]">
                                  {hasMultipleNetworks ? (
                                    <>
                                      <div className="bg-[#252525] flex max-lg:p-[4px] gap-[3px] max-lg:gap-[2px] p-[4px] max-lg:rounded-full rounded-[16px]">
                                        {" "}
                                        {networks.map((network) => (
                                          <img
                                            key={network.nowPaymentsId}
                                            src={network.icon}
                                            alt="net"
                                            className="w-[16px] h-[16px] max-lg:w-[19px] max-lg:h-[19px] lg:min-w-[16px] lg:min-h-[16px] rounded-full"
                                          />
                                        ))}
                                      </div>
                                      <motion.div
                                        transition={{ duration: 0.2 }}
                                        className={clsx(
                                          "max-lg:w-[26px] max-lg:h-[26px] w-[26px] h-[26px] min-w-[26px] min-h-[26px] bg-[#252525] rounded-full before:!rounded-full flex items-center justify-center",
                                          shadowAndBorderClass,
                                        )}
                                      >
                                        {" "}
                                        <ChevronBack
                                          width={12}
                                          height={12}
                                          className={clsx(
                                            "w-[12px] h-[12px] max-lg:w-[15px] max-lg:h-[15px] rotate-[270deg] transition-transform text-white",
                                            {
                                              "!rotate-[90deg]": isExpanded,
                                            },
                                          )}
                                        />
                                      </motion.div>
                                    </>
                                  ) : (
                                    networks[0] && (
                                      <img
                                        src={networks[0].icon}
                                        className="w-[19px] h-[19px] max-lg:w-[19px] max-lg:h-[19px] min-w-[19px] min-h-[19px] rounded-full"
                                        alt="net"
                                      />
                                    )
                                  )}
                                </div>
                              </div>

                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="flex flex-col mt-[12px] max-lg:mt-[6px] max-lg:gap-[5px] gap-[4px] overflow-hidden"
                                  >
                                    {networks.map((network) => (
                                      <motion.button
                                        key={network.nowPaymentsId}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{
                                          duration: 0.2,
                                          delay: 0.1,
                                        }}
                                        className="flex justify-between h-[43px] max-lg:h-[43px] max-lg:p-[12px] max-lg:pl-[16px] pl-[16px] pr-[12px] items-center
                                     w-full bg-[#252525] border-[2px] max-lg:border-[2px] max-lg:rounded-[8px] border-[#333333] text-left
                                    cursor-pointer rounded-[8px] transition-colors hover:bg-[#333333]"
                                        onClick={() =>
                                          handleSelect(
                                            paymentMethods.find(
                                              (p) =>
                                                p.nowPaymentsId ===
                                                network.nowPaymentsId,
                                            )!,
                                          )
                                        }
                                      >
                                        <span className="font-luxora-regular text-14 max-lg:text-16m text-white">
                                          {network.name}
                                        </span>

                                        <img
                                          src={network.icon}
                                          className="w-[19px] h-[19px] max-lg:w-[19px] max-lg:h-[19px] min-w-[19px] min-h-[19px] rounded-full"
                                          alt={network.name}
                                        />
                                      </motion.button>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </button>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </GlowCard>
              </AnimatedGradientBorder>
            </DialogContent>
          </DialogPortal>
        )}
      </AnimatePresence>
    </DialogRoot>
  );
}
